import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Stack, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Skeleton from '@mui/material/Skeleton';
import React, { useEffect, useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import * as yup from 'yup';
import {
	UI_CASH_MANAGEMENT_FEATURE_ENABLED,
	useAuthContext,
} from '../../../context/auth';
import { useConfigContext } from '../../../context/config';
import { CatchUpInvoice } from '../../../types/catchUps';
import { Payment } from '../../../types/payments';
import {
	PaymentReversalReason,
	PolicyInvoice2Response,
} from '../../../types/policy';
import { PremiumReportingInvoice } from '../../../types/premiumReports';
import { FORM_MESSAGE } from '../../fastForm/data/constants';
import { SelectField, StringField } from '../../fastForm/fields';
import {
	REVERSE_PAYMENT_MODAL__BODY_TEXT_TITLE,
	REVERSE_PAYMENT_MODAL__CANCEL,
	REVERSE_PAYMENT_MODAL__COMMENT,
	REVERSE_PAYMENT_MODAL__REASON_NAME,
	REVERSE_PAYMENT_MODAL__SUBMIT,
	REVERSE_PAYMENT_MODAL__TITLE,
	REVERSE_PAYMENT_MODAL__TITLE_LOADING,
} from '../data/constants';

interface FormValues {
	reversal: {
		name: string;
		reason: string;
	};
}

interface ReversePaymentModalProps {
	open: boolean;
	invoice: PolicyInvoice2Response | PremiumReportingInvoice | CatchUpInvoice;
	handleClose: () => void;
	callback: (
		paymentLocator: string,
		policyLocator: string,
		reversalReason: PaymentReversalReason,
	) => Promise<void>;
	isCatchUp?: boolean;
}

const reversalSchema = (detailsRequired: boolean) =>
	yup.object().shape({
		reversal: yup.object().shape({
			name: yup.string().required(FORM_MESSAGE.required),
			reason: detailsRequired
				? yup.string().required(FORM_MESSAGE.required)
				: yup.string(),
		}),
	});

const ReversePaymentModal = (props: ReversePaymentModalProps) => {
	const { open, handleClose, invoice, callback, isCatchUp } = props;

	const authData = useAuthContext();
	const configData = useConfigContext();
	const cashManagementEnabled =
		authData?.auth?.configProperties?.[UI_CASH_MANAGEMENT_FEATURE_ENABLED];
	const paymentReversalConfigs =
		configData.cashManagementConfig?.configuration?.reversals;
	const paymentReversalNames = paymentReversalConfigs?.map(
		(c) => c.displayName,
	);
	const showReversalReason =
		cashManagementEnabled && !!paymentReversalConfigs.length;

	const [formWizard, setFormWizard] = useState<
		'initial' | 'loading' | 'error' | 'success'
	>('initial');
	const [requiresDetail, setRequiresDetail] = useState<boolean>(false);

	const latestPayment =
		invoice.invoiceType === 'premiumReporting'
			? (invoice as PremiumReportingInvoice).payments
			: invoice.invoiceType === 'catchUp'
			? (invoice as CatchUpInvoice).payment
			: (invoice as PolicyInvoice2Response).payments.find(
					(p: Payment) => !p.reversedTimestamp,
			  );

	const methods = useForm<FormValues>({
		defaultValues: {
			reversal: {
				name: '',
				reason: '',
			},
		},
		resolver: showReversalReason && yupResolver(reversalSchema(requiresDetail)),
	});

	const { handleSubmit, control, watch } = methods;

	const reasonName = watch(`reversal.name`);
	const selectedReason = paymentReversalConfigs?.find(
		(p) => p.displayName === reasonName,
	);

	useEffect(() => {
		setRequiresDetail(selectedReason?.requireDetails);
	}, [selectedReason]);

	const onSubmit: SubmitHandler<FormValues> = async (formValues) => {
		setFormWizard('loading');
		try {
			const reversalReasonPayload: PaymentReversalReason = {
				name: selectedReason?.name,
				details: formValues?.reversal?.reason,
			};
			await callback(
				latestPayment.locator,
				invoice?.policyLocator,
				showReversalReason ? reversalReasonPayload : null,
			);
			handleClose();
		} catch (e) {
			handleClose();
		}
	};

	return (
		<Dialog open={open} onClose={handleClose}>
			<Collapse in={formWizard === 'initial'}>
				<FormProvider {...methods}>
					<Box component='form' noValidate onSubmit={handleSubmit(onSubmit)}>
						<DialogTitle>{REVERSE_PAYMENT_MODAL__TITLE}</DialogTitle>
						<DialogContent>
							<Stack direction='column' spacing={2}>
								<Typography>
									{REVERSE_PAYMENT_MODAL__BODY_TEXT_TITLE(
										latestPayment.displayId,
										invoice.displayId,
										isCatchUp,
									)}
								</Typography>
								{showReversalReason && (
									<>
										<SelectField<FormValues>
											order={5}
											name={`reversal.name`}
											title={REVERSE_PAYMENT_MODAL__REASON_NAME}
											disabled={false}
											control={control}
											type='select'
											values={paymentReversalNames}
										/>
										<StringField<FormValues>
											order={7}
											name={`reversal.reason`}
											title={REVERSE_PAYMENT_MODAL__COMMENT}
											multiline
											optional={
												selectedReason ? !selectedReason.requireDetails : true
											}
											disabled={false}
											control={control}
											type='string'
											values={[]}
										/>
									</>
								)}
							</Stack>
						</DialogContent>
						<DialogActions>
							<Button onClick={handleClose} color='neutralButton'>
								{REVERSE_PAYMENT_MODAL__CANCEL}
							</Button>
							<Button
								autoFocus
								color='secondary'
								variant='contained'
								type='submit'
							>
								{REVERSE_PAYMENT_MODAL__SUBMIT}
							</Button>
						</DialogActions>
					</Box>
				</FormProvider>
			</Collapse>
			<Collapse in={formWizard === 'loading'}>
				<DialogTitle>{REVERSE_PAYMENT_MODAL__TITLE_LOADING}</DialogTitle>
				<DialogContent>
					<DialogContentText>
						<Skeleton variant='text' />
					</DialogContentText>
				</DialogContent>
			</Collapse>
		</Dialog>
	);
};

export default ReversePaymentModal;
